import axios from 'axios';
import { apiService } from './ApiService';

const BASE = `${process.env.REACT_APP_API_URL || ''}/api/b2b-portal`;

const api = axios.create({ baseURL: BASE });

// Attach portal access token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('portal_access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-refresh on 401
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    if (error.response?.status === 401) {
      const refresh = localStorage.getItem('portal_refresh_token');
      if (refresh) {
        try {
          const { data } = await axios.post(`${BASE}/auth/refresh`, { refresh_token: refresh });
          localStorage.setItem('portal_access_token', data.access_token);
          error.config.headers.Authorization = `Bearer ${data.access_token}`;
          return axios(error.config);
        } catch {
          localStorage.removeItem('portal_access_token');
          localStorage.removeItem('portal_refresh_token');
          window.location.href = window.location.pathname.replace(/\/orders.*/, '/login');
        }
      }
    }
    return Promise.reject(error);
  },
);

export interface PortalOrder {
  id: number;
  transaction_id: string;
  external_order_id: string;
  name: string;
  vin: string;
  service_type: string;
  status: string;
  source: string;
  parts_in_order: { items: any[] };
  status_notes: any[];
  created_at: string;
  updated_at: string;
}

export interface PortalUser {
  id: number;
  name: string;
  email: string;
  role: string;
  must_change_password: boolean;
  last_login_at: string | null;
}

export interface PortalCompany {
  id: number;
  name: string;
  alias: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  city: string;
  country: string;
}

// ── auth ──────────────────────────────────────────────────────────────────────

export const portalLogin = (email: string, password: string, orgAlias: string) =>
  api.post('/auth/login', { email, password, org_alias: orgAlias }).then((r) => r.data);

export const portalChangePassword = (currentPassword: string, newPassword: string) =>
  api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }).then((r) => r.data);

// ── profile ───────────────────────────────────────────────────────────────────

export const getPortalProfile = () => api.get('/profile').then((r) => r.data);

export const getPortalOrgInfo = (orgAlias: string) =>
  api.get('/org-info', { params: { org_alias: orgAlias } }).then((r) => r.data);

// ── orders ────────────────────────────────────────────────────────────────────

export const listOrders = (params?: { status?: string; limit?: number; offset?: number }) =>
  api.get('/orders', { params }).then((r) => r.data);

export const getOrder = (id: number) => api.get(`/orders/${id}`).then((r) => r.data);

export const createOrder = (payload: Partial<PortalOrder>) =>
  api.post('/orders', payload).then((r) => r.data);

export const cancelOrder = (id: number) => api.delete(`/orders/${id}`).then((r) => r.data);

// ── chat ──────────────────────────────────────────────────────────────────────

export const createPortalChatSession = (language: string) =>
  api.post('/chat/create_session', { language }).then((r) => r.data);

export const getPortalChatBaseUrl = () =>
  `${process.env.REACT_APP_API_URL || ''}/api/b2b-portal`;

export const getPortalChatToken = () =>
  localStorage.getItem('portal_access_token') ?? '';

// ── org admin: portal access management (uses main app token) ────────────────

export const enablePortalAccess = (customerId: number, payload: { temp_password?: string }) =>
  apiService.post<any>(`/api/user/b2b-customers/${customerId}/portal/enable`, payload).then((r: any) => r.data);

export const resetPortalPassword = (customerId: number) =>
  apiService.post<any>(`/api/user/b2b-customers/${customerId}/portal/reset-password`).then((r: any) => r.data);

export const disablePortalAccess = (customerId: number) =>
  apiService.delete<any>(`/api/user/b2b-customers/${customerId}/portal/disable`).then((r: any) => r.data);

// ── org admin: portal user management (uses main app token) ──────────────────
// DEPRECATED — kept for compatibility, use portal access management above

export const listPortalUsers = (customerId: number) =>
  axios.get(`/api/user/b2b-customers/${customerId}/portal-users/`).then((r) => r.data);

export const createPortalUser = (customerId: number, payload: { email: string; name: string; role: string }) =>
  axios.post(`/api/user/b2b-customers/${customerId}/portal-users/`, payload).then((r) => r.data);

export const updatePortalUser = (customerId: number, userId: number, payload: any) =>
  axios.patch(`/api/user/b2b-customers/${customerId}/portal-users/${userId}`, payload).then((r) => r.data);

export const resetPortalUserPassword = (customerId: number, userId: number) =>
  axios.post(`/api/user/b2b-customers/${customerId}/portal-users/${userId}/reset-password`).then((r) => r.data);

export const deactivatePortalUser = (customerId: number, userId: number) =>
  axios.delete(`/api/user/b2b-customers/${customerId}/portal-users/${userId}`).then((r) => r.data);

export default api;
